import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Chip, IconButton, Dialog } from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close';
import * as taskTemplateStyle from '../../kart/fieldmap/tasktemplates/TaskTemplateStyle';
import isEqual from 'lodash.isequal';
import MuiMultiSelectBottomSheetList from '../MuiMultiSelectBottomSheetList';
import MuiDropdownBottomSheet from '../MuiDropdownBottomSheet';
import { Link } from 'react-router-dom';
import { createSvgIcon } from '@mui/material/utils';

const MapIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
    <path d="M12.2286 15.75L7.72864 14.175L4.24114 15.525C3.99114 15.625 3.75989 15.5969 3.54739 15.4406C3.33489 15.2844 3.22864 15.075 3.22864 14.8125V4.3125C3.22864 4.15 3.27551 4.00625 3.36926 3.88125C3.46301 3.75625 3.59114 3.6625 3.75364 3.6L7.72864 2.25L12.2286 3.825L15.7161 2.475C15.9661 2.375 16.1974 2.40313 16.4099 2.55937C16.6224 2.71562 16.7286 2.925 16.7286 3.1875V13.6875C16.7286 13.85 16.6818 13.9937 16.588 14.1187C16.4943 14.2437 16.3661 14.3375 16.2036 14.4L12.2286 15.75ZM11.4786 13.9125V5.1375L8.47864 4.0875V12.8625L11.4786 13.9125ZM12.9786 13.9125L15.2286 13.1625V4.275L12.9786 5.1375V13.9125ZM4.72864 13.725L6.97864 12.8625V4.0875L4.72864 4.8375V13.725Z" fill="#00696D" />
  </svg>,
  'MapIcon',
);

const NewAddTaskModalWindow = (props) => {
  const {
    intl,
    onCreateTask,
    fieldDetails,
    userActivities,
    closeNewTask,
    handleSave,
  } = props;

  const initialFormState = {
    activity: '',
    field: [],
  };

  const [formState, setFormState] = useState(initialFormState);
  const initialStateRef = useRef(initialFormState); 
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState([]);
  const [selectedActivityName, setSelectedActivityName] = useState('');
  const [selectedFieldName, setSelectedFieldName] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false); 

  useEffect(() => {
    setFormState({
      activity: selectedActivityId,
      field: selectedFieldId,
    });
  }, [selectedActivityId, selectedFieldId]);

  useEffect(() => {
    if (onCreateTask) { 
      initialStateRef.current = {
        activity: selectedActivityId, 
        field: selectedFieldId,
      };
    }
  }, [onCreateTask]); 

  const isSaveButtonEnabled = !!selectedActivityId && selectedFieldId.length > 0;

  const uniqueActivities = new Map();

  for (const category in userActivities.userActivitiesList) {
    userActivities.userActivitiesList[category].activities.forEach(activity => {
      if (activity.active) {
        if (!uniqueActivities.has(activity.id)) {
          uniqueActivities.set(activity.id, {
            activityName: activity.activityName,
            activityId: activity.id
          });
        }
      }
    });
  }

  const activityOptions = Array.from(uniqueActivities.values()).map(activity => ({
    label: activity.activityName,
    value: activity.activityId,
  }));

  const fieldListOptions = fieldDetails?.data1?.fields?.map(field => ({
    label: field.fieldName,
    value: field.fieldId,
  })) || [];

  const handleTypeChange = (name, value) => {
    if (name === 'activity') {
      setSelectedActivityId(value);
    } else if (name === 'field') {
      let newSelectedValue = value;
      if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0])) {
        newSelectedValue = value.flat();
      } else if (typeof value === 'string') {
        newSelectedValue = value.split(',');
      }
      setSelectedFieldId(newSelectedValue);
    }
  };

  useEffect(() => {
    if (selectedActivityId && activityOptions) {
      const activity = activityOptions.find(
        (option) => option.value === selectedActivityId
      );
      setSelectedActivityName(activity ? activity.label : '');
    } else {
      setSelectedActivityName('');
    }
  }, [selectedActivityId, activityOptions]);

  useEffect(() => {
    if (selectedFieldId && selectedFieldId.length > 0 && fieldDetails.data1?.fields) {
      const selectedNames = selectedFieldId.map(id => {
        const field = fieldDetails.data1.fields.find(f => f.fieldId === id);
        return field ? field.fieldName : '';
      }).filter(Boolean);
      setSelectedFieldName(selectedNames);
    } else {
      setSelectedFieldName([]);
    }
  }, [selectedFieldId, fieldDetails]);

  const resetAndCloseMainModal = () => {
    setSelectedActivityId('');
    setSelectedFieldId([]);
    setSelectedActivityName('');
    setSelectedFieldName([]);
    setFormState(initialFormState); 
    initialStateRef.current = initialFormState;
    closeNewTask(); 
  };

  const handleMainCancel = () => {
    const currentFormState = {
      activity: selectedActivityId,
      field: selectedFieldId,
    };

    const hasChangesMade = !isEqual(currentFormState, initialStateRef.current);

    if (hasChangesMade) {
      setIsConfirmationModalOpen(true); 
    } else {
      resetAndCloseMainModal();
    }
  };

  const handleConfirmCancel = () => {
    setIsConfirmationModalOpen(false); 
    resetAndCloseMainModal();
  };

  const handleContinueRegistration = () => {
    setIsConfirmationModalOpen(false); 
  };

  const handleSaveClick = () => {
    handleSave(selectedActivityId, selectedFieldId, selectedActivityName, selectedFieldName);
    resetAndCloseMainModal();
  };

  return (
    <>
      {onCreateTask && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1299,
          }}
          onClick={handleMainCancel} 
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          background: '#F2F4F4',
          zIndex: 1300,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          display: onCreateTask ? 'flex' : 'none',
          flexDirection: 'column',
          maxHeight: '80vh',
          overflowY: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}
      >
        <Box sx={{ textAlign: 'center', padding: '10px 0' }}>
          <Box sx={{ width: 32, height: 4, opacity: 0.4, backgroundColor: '#6F7979', borderRadius: 2, margin: '0 auto' }} />
        </Box>
        <Box sx={{ padding: '24px 24px 0px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              onClick={handleMainCancel} 
              sx={{
                padding: 0,
                marginRight: '8px',
                color: 'inherit',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              style={{
                ...taskTemplateStyle.modalDialogTitle,
              }}
            >
              {intl.formatMessage({ id: "NEW_REGISTRATION" })}
            </Typography>
          </Box>
          <Box sx={{ paddingTop: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <MuiDropdownBottomSheet
                label={intl.formatMessage({ id: 'ACTIVITY_REGISTERATION' })}
                subtitle={intl.formatMessage({ id: 'REGISTRATION' })}
                defaultValue={formState.activity || ''}
                options={activityOptions}
                onChange={handleTypeChange}
                value={selectedActivityId}
                fieldName="activity"
              />
            </Box>
            <Box sx={{ paddingBottom: '0px' }}>
              <MuiMultiSelectBottomSheetList
                label={intl.formatMessage({ id: 'FIELD_SELECTION' })}
                subTitle={intl.formatMessage({ id: 'FIELD_SELECTION' })}
                options={fieldListOptions}
                defaultValue={formState.field || []}
                onChange={handleTypeChange}
                value={selectedFieldId}
                fieldName="field"
              />
            </Box>
          </Box>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '8px' }}>
          <Chip
            label={intl.formatMessage({ id: 'SUB_BALES_SAVE_BUTTON' })}
            clickable
            onClick={handleSaveClick}
            sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              height: '40px',
              borderRadius: '100px',
              padding: '0px 16px',
              fontSize: 14,
              color: isSaveButtonEnabled ? '#ffffff' : '#111111',
              backgroundColor: isSaveButtonEnabled ? '#00696D' : '#999999',
              '&:hover, &:focus': {
                backgroundColor: isSaveButtonEnabled ? '#005457' : '#999999',
              },
            }}
            disabled={!isSaveButtonEnabled}
            disableRipple
          />
        </Box>
        <Link to="/kart/fieldmap">
          <Box paddingBottom='16px'>
            <Box sx={{
              display: 'flex',
              padding: '10px 16px 10px 12px',
              justifyContent: 'center',
              alignItems: 'center',
              flex: '1 0 0',
              alignSelf: 'stretch',
              cursor: 'pointer'
            }}>
              <MapIcon sx={{ marginRight: '8px' }} />
              <Typography sx={{
                paddingTop: '8px',
                paddingBottom: '8px',
                color: 'var(--M3-sys-light-primary, #00696D)',
                textAlign: 'center',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '500',
                lineHeight: '20px',
                letterSpacing: '0.1px'
              }}>{intl.formatMessage({ id: 'REGISTER_FROM_MAP' })}</Typography>
            </Box>
          </Box>
        </Link>
      </Box>

      {/* Confirmation Dialog for Unsaved Changes */}
      <Dialog
        onClose={() => { }} 
        open={isConfirmationModalOpen}
        PaperProps={{
          style: {
            backgroundColor: '#E6E9E8',
            borderRadius: "28px",
            width: "312px",
            minWidth: "280px",
            maxWidth: "312px",
            maxHeight: '30vh'
          }
        }}
      >
        <Box style={taskTemplateStyle.cancelModalBox}>
          <Typography style={taskTemplateStyle.cancelModalTitle}>
            {intl.formatMessage({ id: 'TASK_CANCEL_MODAL_TITLE' })}
          </Typography>
          <Typography style={taskTemplateStyle.cancelModalSubTitle}>
            {intl.formatMessage({ id: 'TASK_CANCEL_MODAL_SUB_TITLE' })}
          </Typography>
        </Box>
        <Box style={taskTemplateStyle.cancelModalBoxButton}>
          <Chip
            label={intl.formatMessage({ id: 'cancel' })}
            clickable
            style={taskTemplateStyle.cancelButtonStyle}
            sx={{
              backgroundColor: 'transparent',
              '&:hover, &:focus': {
                backgroundColor: 'transparent',
              }
            }}
            disableRipple
            onClick={handleConfirmCancel} 
          />
          <Chip
            label={intl.formatMessage({
              id: 'TASK_CONTINUE_REGISTRATION'
            })}
            clickable
            style={taskTemplateStyle.cancelButtonStyle}
            sx={{
              backgroundColor: 'transparent',
              '&:hover, &:focus': {
                backgroundColor: 'transparent',
              }
            }}
            disableRipple
            onClick={handleContinueRegistration} 
          />
        </Box>
      </Dialog>
    </>
  );
};

export default NewAddTaskModalWindow;